import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {attributeHelpCenterClick} from '../../utils/HelpCenterAttribution';
import {Check} from '../icons';
import {hasTaskMedia, TaskMedia} from './media/TaskMedia';
import {SOURCE_MANUAL, STATE_DONE, STATE_PENDING, STATE_SKIPPED, TASK_JOINED, taskCopyKey} from './state/learnModel';
import {doneNote, taskTitle} from './learnText';

const LEARN_LINES = ['learn_1', 'learn_2'];

/**
 * Una tarea de una pista. Patrón disclosure: la cabecera es un `<button aria-expanded>` (Enter y
 * Espacio los da el botón nativo) y el cuerpo existe sólo abierta. La página abre UNA a la vez.
 *
 * El estado se dice con ícono Y con texto («Hecho el 30/09», «Omitida», y «Pendiente» para el lector
 * de pantalla), no sólo con el verde del círculo.
 *
 * `joined_agency` no se abre: viene hecha y no tiene nada adentro.
 *
 * `model`: de qué claves salen los textos (la tarea de alertas con el Centro es otra, `taskCopyKey`).
 * `start`: a dónde lleva el botón (`startTarget`).
 */
export function LearnTask({task, model, agencyName, open, saving, justDone, start, onToggle, onSkip, onUnskip,
                              onComplete, onUncomplete}) {
    const {t, i18n} = useTranslation();
    const title = taskTitle(t, task.key, agencyName, model);
    const copy = taskCopyKey(task);
    const bodyId = `ld-task-${task.key}`;
    const isDone = task.state === STATE_DONE;
    const isSkipped = task.state === STATE_SKIPPED;
    const note = isDone ? doneNote(t, task, i18n.language) : (isSkipped ? '' : t(`learn_dashbo.tasks.${copy}.why`));
    const className = `ld-task -${task.state}${open ? ' -open' : ''}${justDone ? ' -just-done' : ''}`;

    const dot = (
        <span className="ld-task__dot" aria-hidden="true">
            {isDone && <Check size={12} weight="bold"/>}
        </span>
    );

    if (task.key === TASK_JOINED) {
        return (
            <li className={className}>
                <div className="ld-task__head -static">
                    {dot}
                    <span className="ld-task__titles">
                        <span className="ld-task__title">{title}</span>
                        <span className="visually-hidden">{t('learn_dashbo.task.state.done')}</span>
                    </span>
                </div>
            </li>
        );
    }

    return (
        <li className={className}>
            <button type="button" className="ld-task__head" aria-expanded={open} aria-controls={bodyId}
                    onClick={onToggle}>
                {dot}
                <span className="ld-task__titles">
                    <span className="ld-task__title">{title}</span>
                    {/* Cerrada, la pendiente adelanta para qué sirve (el mockup); abierta, el «para qué» ya
                        está en el cuerpo y no se repite. */}
                    {note && !(open && task.state === STATE_PENDING) && (
                        <span className={`ld-task__note${isDone ? ' -ok' : ''}`}>{note}</span>
                    )}
                    {task.state === STATE_PENDING && (
                        <span className="visually-hidden">{t('learn_dashbo.task.state.pending')}</span>
                    )}
                </span>
                <span className="ld-task__aside">
                    {isDone && t('learn_dashbo.task.review')}
                    {isSkipped && t('learn_dashbo.task.state.skipped')}
                    {task.state === STATE_PENDING && task.minutes > 0
                        && t('learn_dashbo.task.minutes', {count: task.minutes})}
                </span>
            </button>

            {open && (
                <div id={bodyId} className={`ld-task__body${hasTaskMedia(task) ? ' -with-media' : ''}`}>
                    <TaskBody task={task} title={title} copy={copy} saving={saving} start={start}
                              onSkip={onSkip} onUnskip={onUnskip} onComplete={onComplete} onUncomplete={onUncomplete}/>
                </div>
            )}
        </li>
    );
}

function TaskBody({task, title, copy, saving, start, onSkip, onUnskip, onComplete, onUncomplete}) {
    const {t, i18n} = useTranslation();
    const isPending = task.state === STATE_PENDING;
    // «Qué vas a aprender» es opcional por tarea (copy.md §2): sin la clave, no se dibuja la lista.
    const learnLines = LEARN_LINES
        .map((line) => `learn_dashbo.tasks.${copy}.${line}`)
        .filter((key) => i18n.exists(key))
        .map((key) => t(key));

    return (
        <>
            <div className="ld-task__text">
                <p className="ld-task__why">{t(`learn_dashbo.tasks.${copy}.why`)}</p>
                {learnLines.length > 0 && (
                    <ul className="ld-task__learn">
                        {learnLines.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                )}
                <div className="ld-task__actions">
                    {/* UN botón (C-04 del recorrido con Pablo): «Hacerlo ahora» y «Mostrarme cómo» iban a
                        la misma pantalla y había que decidir de antemano si se iba a necesitar ayuda. La
                        primera vez abre la guía (si la tarea tiene); después sólo lleva (`startTarget`). */}
                    {start && (
                        <Link className="ld-btn -primary" to={start.to} data-guided={start.guided ? 'true' : 'false'}>
                            {task.state === STATE_DONE ? t('learn_dashbo.task.go_again') : t('learn_dashbo.task.start')}
                        </Link>
                    )}
                    {/* Al Centro de Ayuda con la atribución (quién lo abre y desde qué pantalla), como
                        el link de Ayuda del menú. `LinkToArticle` no sirve: revienta con claves que no
                        conoce. */}
                    {task.articleUrl && (
                        <a className="ld-quiet" href={task.articleUrl} data-help-url={task.articleUrl}
                           target="_blank" rel="noopener noreferrer"
                           onMouseDown={attributeHelpCenterClick} onClick={attributeHelpCenterClick}>
                            {t('learn_dashbo.task.read_article')}
                        </a>
                    )}
                    {isPending && task.canMark && (
                        <button type="button" className="ld-quiet" onClick={onComplete} disabled={saving}>
                            {t('learn_dashbo.task.mark_done')}
                        </button>
                    )}
                    {isPending && task.canSkip && (
                        <button type="button" className="ld-quiet" onClick={onSkip} disabled={saving}>
                            {t('learn_dashbo.task.skip')}
                        </button>
                    )}
                    {task.state === STATE_SKIPPED && (
                        <button type="button" className="ld-quiet" onClick={onUnskip} disabled={saving}>
                            {t('learn_dashbo.task.unskip')}
                        </button>
                    )}
                    {/* Sólo se deshace lo marcado a mano: un tilde detectado es para siempre (regla 1). */}
                    {task.state === STATE_DONE && task.source === SOURCE_MANUAL && task.canMark && (
                        <button type="button" className="ld-quiet" onClick={onUncomplete} disabled={saving}>
                            {t('learn_dashbo.task.mark_pending')}
                        </button>
                    )}
                </div>
                {isPending && <p className="ld-task__hint">{t(`learn_dashbo.tasks.${copy}.hint`)}</p>}
            </div>
            {hasTaskMedia(task) && (
                <div className="ld-task__media">
                    <TaskMedia task={task} title={title}/>
                </div>
            )}
        </>
    );
}
